<template>
    <div>
        <v-dialog
            :value="isOpen"
            scrollable
            max-width="700px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <template v-if="!isNewMachine">
                            Edycja maszyny <strong>{{ machineData.name }}</strong>
                        </template>
                        <template v-else>
                            Nowa maszyna
                        </template>
                    </h1>
                </v-card-title>

                <v-card-text class="text-center">
                    <v-row>
                        <v-col cols="12" sm="8">
                            <v-text-field
                                v-model="machine.name"
                                label="Nazwa"
                                :error-messages="machineNameErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="machine.symbol"
                                label="Symbol"
                                :error-messages="symbolErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="machine.code"
                                label="Kod"
                                outlined
                                dense
                                @input="changeCode"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="machine.mpk"
                                label="Mpk"
                                :error-messages="mpkErrors"
                                outlined
                                dense
                                @input="changeMpk"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                :value="machine.numberOfWorkers"
                                label="Ilość pracowników"
                                :error-messages="numberOfWorkersErrors"
                                outlined
                                dense
                                required
                                @input="changeNumberOfWorkers"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                v-model="machine.productionDepartmentId"
                                :items="productionDepartments"
                                label="Dział"
                                placeholder="Dział"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono działu"
                                :error-messages="productionDepartmentIdErrors"
                                outlined
                                dense
                                required
                                @change="changeProductionDepartment"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="machine.productionSubDepartmentId"
                                label="Wydział"
                                :items="productionSubDepartments"
                                :disabled="!machine.productionDepartmentId"
                                item-value="id"
                                item-text="name"
                                outlined
                                dense
                            />
                        </v-col>

                        <v-col cols="12">
                            <v-select
                                v-model="machine.machineDURGroupId"
                                label="Grupa DUR"
                                :items="machineDurGroups"
                                item-value="id"
                                item-text="name"
                                :error-messages="machineDURGroupIdErrors"
                                outlined
                                dense
                            />
                        </v-col>

                        <v-col cols="12">
                            <v-autocomplete
                                v-model="machine.productionProcess"
                                :items="productionProcesses"
                                :search-input.sync="search"
                                label="Procesy produkcyjne"
                                placeholder="Wybierz procesy produkcyjne"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono procesu"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                @change="changeProductionProcess"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        small
                                        @update:active="removeProductionProcess(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <v-col v-if="hasPrintingProcess" cols="12">
                            <v-autocomplete
                                v-model="machine.printType"
                                :items="machinePrintTypes"
                                :search-input.sync="searchPrintTypes"
                                label="Rodzaj druku"
                                placeholder="Rodzaj druku"
                                item-value="id"
                                item-text="name"
                                no-data-text="Brak danych"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                @change="searchPrintTypes = null"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        small
                                        @update:active="removePrintTypes(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <v-col v-if="hasPrintingProcess" cols="12">
                            <v-autocomplete
                                v-model="machine.sheetFormat"
                                :items="machineSheetFormats"
                                :search-input.sync="searchSheetFormat"
                                label="Format arkusza"
                                placeholder="Format arkusza"
                                item-value="id"
                                item-text="name"
                                no-data-text="Brak danych"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                @change="searchSheetFormat = null"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        small
                                        @update:active="removeSheetFormats(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <v-col v-if="hasPrintingProcess" cols="12">
                            <v-autocomplete
                                v-model="machine.plateType"
                                :items="machinePlateTypes"
                                :search-input.sync="searchPlateTypes"
                                label="Rodzaj płyt"
                                placeholder="Rodzaj płyt"
                                item-value="id"
                                item-text="name"
                                no-data-text="Brak danych"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                @change="searchPlateTypes = null"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        small
                                        @update:active="removePlateTypes(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-checkbox
                        v-model="machine.hasVarnishingTower"
                        label="Ma wieże lakierującą"
                        hide-details
                    />
                    <v-checkbox
                        v-model="machine.canReportAccident"
                        label="Zgłaszanie awarii"
                        hide-details
                    />

                    <v-checkbox
                        v-model="machine.showInDashboard"
                        label="Pokazuj na dashboard"
                        hide-details
                    />

                    <v-checkbox
                        v-model="machine.isVisible"
                        label="Czy widoczna?"
                        hide-details
                    />
                    <v-checkbox
                        v-model="machine.isVirtual"
                        label="Wirtualna"
                        hide-details
                    />
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        :loading="actionLoading"
                        @click="saveMachine"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { appConfig } from '@/config'
import { required, between, integer } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'SaveMachineDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        machineData: {
            type: Object,
            default: null
        },
        machinePrintTypes: {
            type: Array,
            default: null
        },
        machineSheetFormats: {
            type: Array,
            default: null
        },
        machinePlateTypes: {
            type: Array,
            default: null
        },
        productionDepartments: {
            type: Array,
            default: null
        },
        productionProcesses: {
            type: Array,
            default: null
        },
        machineDurGroups: {
            type: Array,
            default: null
        }
    },
    data() {
        return {
            machine: {
                name: null,
                canReportAccident: false,
                productionDepartmentId: null,
                productionSubDepartmentId: null,
                productionProcess: [],
                printType: [],
                sheetFormat: [],
                plateType: [],
                isVisible: false,
                symbol: null,
                numberOfWorkers: null,
                showInDashboard: false,
                machineDURGroupId: null,
                isVirtual: false,
                code: null,
                mpk: null,
                hasVarnishingTower: false
            },
            productionSubDepartments: [],
            hasPrintingProcess: false,
            actionLoading: false,
            search: null,
            searchPlateTypes: null,
            searchSheetFormat: null,
            searchPrintTypes: null
        }
    },
    validations() {
        const validations = {
            machine: {
                name: {
                    required
                },
                productionDepartmentId: {
                    required
                },
                symbol: {
                    required
                },
                machineDURGroupId: {
                    required
                },
                mpk: {
                    integer
                }
            }
        }

        if (this.machine.numberOfWorkers) {
            validations.machine['numberOfWorkers'] = {
                between: between(1, this.maxNumberOfWorkers)
            }
        }

        return validations
    },
    computed: {
        isNewMachine() {
            return isEmpty(this.machineData)
        },
        maxNumberOfWorkers() {
            return appConfig.configurations.machines.numberOfWorkersMaxCount
        },

        // Walidacje
        machineNameErrors() {
            const errors = []

            if (!this.$v.machine.name.$dirty) return errors
            !this.$v.machine.name.required && errors.push('Proszę podać nazwę maszyny.')

            return errors
        },
        productionDepartmentIdErrors() {
            const errors = []

            if (!this.$v.machine.productionDepartmentId.$dirty) return errors
            !this.$v.machine.productionDepartmentId.required && errors.push('Proszę wybrać dział.')

            return errors
        },
        symbolErrors() {
            const errors = []

            if (!this.$v.machine.symbol.$dirty) return errors
            !this.$v.machine.symbol.required && errors.push('Proszę podać symbol maszyny.')

            return errors
        },
        numberOfWorkersErrors() {
            if (!this.machine.numberOfWorkers) return
            const errors = []

            if (!this.$v.machine.numberOfWorkers.$dirty) return errors

            if (!this.$v.machine.numberOfWorkers.between) {
                errors.push(`Proszę podać wartość z zakresu 1-${this.maxNumberOfWorkers}`)
            }

            return errors
        },
        machineDURGroupIdErrors() {
            const errors = []

            if (!this.$v.machine.machineDURGroupId.$dirty) return errors
            !this.$v.machine.machineDURGroupId.required && errors.push('Proszę wybrać grupę DUR.')

            return errors
        },
        mpkErrors() {
            if (!this.machine.mpk) return
            const errors = []

            if (!this.$v.machine.mpk.$dirty) return errors
            !this.$v.machine.mpk.integer && errors.push('Proszę podać wartość mpk jako liczbę naturalną.')

            return errors
        },
    },
    watch: {
        machineData(value) {
            if (!isEmpty(value)) {
                this.machine = JSON.parse(JSON.stringify(value))
                this.productionSubDepartments = value.productionDepartment.productionSubDepartments
                this.hasPrintingProcess = value.productionProcess.includes(appConfig.common.processes.printTaskId)
            }
        }
    },
    methods: {
        saveMachine() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let action = null

            this.isNewMachine ? action = 'createMachine' : action = 'updateMachine'

            this.actionLoading = true
            this.$store.dispatch(`configurations/${action}`, this.machine)
                .then(() => {
                    this.$emit('on-confirm', action)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onClose()
                })
        },
        changeNumberOfWorkers(value) {
            this.machine.numberOfWorkers = value || null
        },
        changeMpk(value) {
            this.machine.mpk = value || null
        },
        changeCode(value) {
            this.machine.code = value || null
        },
        changeProductionProcess(processes) {
            this.hasPrintingProcess = processes.includes(appConfig.common.processes.printTaskId)
            if (!this.hasPrintingProcess) {
                this.machine.printType = []
                this.machine.sheetFormat = []
                this.machine.plateType = []
            }
            this.search = null
        },
        removeProductionProcess(item) {
            this.removeItemFromMachine(item, this.machine.productionProcess)
            this.changeProductionProcess(this.machine.productionProcess)
        },
        removePrintTypes(item) {
            this.removeItemFromMachine(item, this.machine.printType)
        },
        removeSheetFormats(item) {
            this.removeItemFromMachine(item, this.machine.sheetFormat)
        },
        removePlateTypes(item) {
            this.removeItemFromMachine(item, this.machine.plateType)
        },
        removeItemFromMachine(item, array) {
            array.splice(array.indexOf(item.id), 1)
        },
        onClose() {
            setTimeout(() => {
                this.machine.name = null
                this.machine.productionDepartmentId = null
                this.machine.productionSubDepartmentId = null
                this.machine.productionProcess = []
                this.machine.canReportAccident = false
                this.machine.isVisible = false
                this.machine.symbol = null,
                this.machine.numberOfWorkers = null
                this.machine.printType = [],
                this.machine.sheetFormat = [],
                this.machine.plateType = [],
                this.machine.machineDURGroupId = null
                this.productionSubDepartments = [],
                this.hasPrintingProcess = false,
                this.isVirtual = false
                this.machine.code = null
                this.machine.mpk = null
                this.machine.hasVarnishingTower = false
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        },
        changeProductionDepartment() {
            let department = this.productionDepartments.find(item => item.id === this.machine.productionDepartmentId)

            this.productionSubDepartments = department.productionSubDepartments
            this.productionSubDepartmentId = null
        },
    }
}
</script>
